import { initContract } from "@ts-rest/core";
import { StreamableFile } from "@nestjs/common";
import { z } from "zod";
import { ErrorMessageSchema } from "../schemas/error.schema";
import { invoiceExportColumnSchema } from "../schemas/domains/invoice/invoice-export.schema";
import { createInvoicesBodySchema } from "../schemas/domains/invoice/create-invoice.schema";
import { patchImporterInvoicesBodySchema } from "../schemas/domains/invoice/patch-importer-invoices.schema";
import { InvoiceScheduleChange } from "../schemas/domains/invoice/invoice-schedule-change.schema";
import { ScheduleLifecyclePeriodLockedError } from "../schemas/domains/accounting/scheduleLifecycle.schema";
import { bulkUpdateInvoiceStatusBodySchema } from "../schemas/domains/invoice/bulk-update-invoice-status.schema";
import { bulkUpdateInvoiceCounterpartyBodySchema } from "../schemas/domains/invoice/bulk-update-invoice-counterparty.schema";
import { bulkDownloadInvoicePdfBodySchema } from "../schemas/domains/invoice/bulk-download-invoice-pdf.schema";
import { updateInvoiceLineItemBodySchema } from "../schemas/domains/invoice/invoice-line-item.schema";
import { updateInvoiceBodySchema } from "../schemas/domains/invoice/update-invoice.schema";
import { invoiceDocumentBodySchema } from "../schemas/domains/invoice/invoice-document.schema";
import { invoicesPageResponseSchema } from "../schemas/domains/invoice/invoice-response.schema";
import { invoiceListStatisticsResponseSchema } from "../schemas/domains/invoice/invoice-list-statistics.schema";
import { InvoiceFiltersSchema } from "../schemas/domains/invoice/invoice-filters.schema";
import { accountOverridesRequestSchema } from "../schemas/domains/accounting/account-override.schema";
import {
    documentAccrualMatchesResponseSchema,
    invoiceAccrualMatchSummariesQuerySchema,
    invoiceAccrualMatchSummariesResponseSchema,
} from "../schemas/domains/accrualReview/accrual-review.schema";
import { suggestedAccrualMatchTransactionsForInvoiceQuerySchema } from "../schemas/domains/accrualReview/suggested-accrual-match-transactions-for-invoice.schema";
import { getTransactionsResponseWithCountSchema } from "../schemas/transaction.schema";
import { documentImportedSettlementsResponseSchema } from "../schemas/domains/accrualReview/imported-settlement-display.schema";
import { lastInvoiceNumberQueryParamsSchema } from "../schemas/domains/invoice/native-invoice.schema";
import {
    InvoiceBase,
    InvoicesByCounterpartyItem,
    InvoiceStats,
    InvoiceViewOptionGroup,
} from "../types/invoice";

const c = initContract();

const InvoiceWorkspacePathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
});

const InvoiceLineItemPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    lineItemId: z.string().uuid(),
});

const InvoiceDocumentPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    invoiceId: z.string().uuid(),
});

export { InvoiceFiltersSchema };
export type { InvoiceFilters } from "../schemas/domains/invoice/invoice-filters.schema";

const InvoiceQueryParamsSchema = z.object({
    filters: InvoiceFiltersSchema.optional(),
    sort: z
        .object({
            field: z.string(),
            order: z.enum(["ASC", "DESC"]),
        })
        .optional(),
    page: z.number(),
    limit: z.number(),
});
export type InvoiceQueryParams = z.infer<typeof InvoiceQueryParamsSchema>;

export const InvoiceStatsParamsSchema = z.object({
    filters: InvoiceFiltersSchema.optional(),
});
export type InvoiceStatsParams = z.infer<typeof InvoiceStatsParamsSchema>;

export const InvoicesByCounterpartyQueryParamsSchema = z.object({
    filters: InvoiceFiltersSchema.optional(),
    sort: z
        .object({
            field: z.string(),
            order: z.enum(["ASC", "DESC"]),
        })
        .optional(),
    page: z.number(),
    limit: z.number(),
});
export type InvoicesByCounterpartyQueryParams = z.infer<
    typeof InvoicesByCounterpartyQueryParamsSchema
>;

export const InvoiceExportQueryParamsSchema = z.object({
    filters: InvoiceFiltersSchema.optional(),
    sort: z
        .object({
            field: z.string(),
            order: z.enum(["ASC", "DESC"]),
        })
        .optional(),
    groupBy: z.nativeEnum(InvoiceViewOptionGroup),
    columns: z.array(invoiceExportColumnSchema).min(1),
});
export type InvoiceExportQueryParams = z.infer<
    typeof InvoiceExportQueryParamsSchema
>;

const BulkUpdateInvoiceLineItemsBodySchema = z
    .object({
        classIds: z.array(z.string().uuid()).optional(),
        accountOverrides: accountOverridesRequestSchema.optional(),
        accountsReceivableOverrides: accountOverridesRequestSchema.optional(),
        // Transitional legacy fields. New callers should use the
        // per-ledger override arrays; these resolve to the entity's
        // primary accrual ledger server-side.
        accountId: z.string().uuid().nullable().optional(),
        accountsReceivableId: z.string().uuid().nullable().optional(),
        lineItemIds: z.array(z.string().uuid()).optional(),
        filters: InvoiceFiltersSchema.optional(),
    })
    .refine(
        ({
            classIds,
            accountOverrides,
            accountsReceivableOverrides,
            accountId,
            accountsReceivableId,
        }) =>
            classIds !== undefined ||
            accountOverrides !== undefined ||
            accountsReceivableOverrides !== undefined ||
            accountId !== undefined ||
            accountsReceivableId !== undefined,
        {
            message:
                "classIds, accountOverrides, accountsReceivableOverrides, accountId or accountsReceivableId must be provided",
        },
    )
    .refine(
        ({ lineItemIds, filters }) =>
            (lineItemIds?.length ?? 0) > 0 || filters !== undefined,
        {
            message: "lineItemIds or filters must be provided",
        },
    );

export const invoiceContract = c.router(
    {
        createInvoices: {
            method: "POST",
            path: "/:workspaceId",
            responses: {
                201: c.type<{ data: InvoiceBase[] }>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: createInvoicesBodySchema,
            pathParams: InvoiceWorkspacePathParamsSchema,
        },
        createInvoicesMcp: {
            method: "POST",
            path: "/:workspaceId/mcp",
            responses: {
                201: c.type<{ data: InvoiceBase[] }>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: createInvoicesBodySchema,
            pathParams: InvoiceWorkspacePathParamsSchema,
        },
        patchMcpInvoices: {
            method: "PUT",
            path: "/:workspaceId/mcp",
            responses: {
                200: c.type<{
                    data: InvoiceBase[];
                    scheduleChanges: InvoiceScheduleChange[];
                }>(),
                400: c.type<{ message: string }>(),
                409: c.type<ScheduleLifecyclePeriodLockedError>(),
                404: c.type<object>(),
            },
            body: patchImporterInvoicesBodySchema,
            pathParams: InvoiceWorkspacePathParamsSchema,
        },
        getInvoices: {
            method: "GET",
            path: "/:workspaceId",
            responses: {
                200: invoicesPageResponseSchema,
            },
            query: InvoiceQueryParamsSchema,
        },
        getInvoiceStats: {
            method: "GET",
            path: "/:workspaceId/stats",
            responses: {
                200: c.type<{
                    data: InvoiceStats;
                }>(),
            },
            query: InvoiceStatsParamsSchema,
        },
        getInvoiceListStatistics: {
            method: "GET",
            path: "/:workspaceId/list-statistics",
            pathParams: InvoiceWorkspacePathParamsSchema,
            responses: {
                200: z.object({
                    data: invoiceListStatisticsResponseSchema,
                }),
                404: ErrorMessageSchema,
            },
            query: InvoiceStatsParamsSchema,
        },
        getInvoicesByCounterparty: {
            method: "GET",
            path: "/:workspaceId/by-counterparty",
            responses: {
                200: c.type<{
                    total: number;
                    pageCount: number;
                    data: InvoicesByCounterpartyItem[];
                }>(),
            },
            query: InvoicesByCounterpartyQueryParamsSchema,
        },
        exportInvoicesXlsx: {
            method: "GET",
            path: "/:workspaceId/xlsx",
            pathParams: InvoiceWorkspacePathParamsSchema,
            query: InvoiceExportQueryParamsSchema,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getLastInvoiceNumber: {
            method: "GET",
            path: "/:workspaceId/last-number",
            responses: {
                200: c.type<{ invoiceNumber: string | null }>(),
                404: c.type<object>(),
            },
            pathParams: InvoiceWorkspacePathParamsSchema,
            query: lastInvoiceNumberQueryParamsSchema,
        },
        patchImporterInvoices: {
            method: "PUT",
            path: "/:workspaceId/importer",
            responses: {
                200: c.type<{
                    data: InvoiceBase[];
                    scheduleChanges: InvoiceScheduleChange[];
                }>(),
                400: c.type<{ message: string }>(),
                409: c.type<ScheduleLifecyclePeriodLockedError>(),
                404: c.type<object>(),
            },
            body: patchImporterInvoicesBodySchema,
            pathParams: InvoiceWorkspacePathParamsSchema,
        },
        bulkUpdateInvoiceLineItems: {
            method: "PATCH",
            path: "/:workspaceId/line-items/bulk",
            responses: {
                200: c.type<object>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: BulkUpdateInvoiceLineItemsBodySchema,
            pathParams: InvoiceWorkspacePathParamsSchema,
        },
        updateInvoiceLineItem: {
            method: "PATCH",
            path: "/:workspaceId/line-items/:lineItemId",
            body: updateInvoiceLineItemBodySchema,
            responses: {
                200: c.type<object>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
                409: c.type<{ message: string }>(),
            },
            pathParams: InvoiceLineItemPathParamsSchema,
        },
        updateInvoice: {
            method: "PATCH",
            path: "/:workspaceId/:invoiceId",
            responses: {
                200: c.type<object>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
                409: c.type<{ message: string }>(),
            },
            body: updateInvoiceBodySchema,
            pathParams: InvoiceDocumentPathParamsSchema,
        },
        bulkUpdateInvoiceStatus: {
            method: "PATCH",
            // Two path segments after :workspaceId so this route never collides
            // with the single-segment "/:workspaceId/:invoiceId" updateInvoice
            // route (Express matches route registration order, not
            // specificity, so a single-segment "/bulk-status" here would be
            // captured by updateInvoice's :invoiceId param first).
            path: "/:workspaceId/bulk/status",
            responses: {
                200: c.type<{
                    succeeded: string[];
                    failed: Array<{ invoiceId: string; message: string }>;
                }>(),
                400: c.type<{ message: string }>(),
            },
            body: bulkUpdateInvoiceStatusBodySchema,
            pathParams: InvoiceWorkspacePathParamsSchema,
        },
        bulkUpdateInvoiceCounterparty: {
            method: "PATCH",
            // Same two-segment path shape as bulkUpdateInvoiceStatus, for the
            // same route-collision reason.
            path: "/:workspaceId/bulk/counterparty",
            responses: {
                200: c.type<{
                    succeeded: string[];
                    failed: Array<{ invoiceId: string; message: string }>;
                }>(),
                400: c.type<{ message: string }>(),
            },
            body: bulkUpdateInvoiceCounterpartyBodySchema,
            pathParams: InvoiceWorkspacePathParamsSchema,
        },
        bulkDownloadInvoicePdf: {
            method: "POST",
            // Same two-segment path shape as bulkUpdateInvoiceStatus, for the
            // same route-collision reason.
            path: "/:workspaceId/bulk/pdf",
            responses: {
                200: z.object({}),
                404: ErrorMessageSchema,
            },
            body: bulkDownloadInvoicePdfBodySchema,
            pathParams: InvoiceWorkspacePathParamsSchema,
        },
        linkDocumentToInvoice: {
            method: "POST",
            path: "/:workspaceId/:invoiceId/documents/link",
            responses: {
                200: c.type<{
                    data: { financialDocumentIds: number[] };
                }>(),
                404: c.type<object>(),
            },
            body: invoiceDocumentBodySchema,
            pathParams: InvoiceDocumentPathParamsSchema,
        },
        unlinkDocumentFromInvoice: {
            method: "POST",
            path: "/:workspaceId/:invoiceId/documents/unlink",
            responses: {
                200: c.type<{
                    data: { financialDocumentIds: number[] };
                }>(),
                404: c.type<object>(),
            },
            body: invoiceDocumentBodySchema,
            pathParams: InvoiceDocumentPathParamsSchema,
        },
        deleteInvoice: {
            method: "DELETE",
            path: "/:workspaceId/:invoiceId",
            responses: {
                200: c.type<object>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            pathParams: InvoiceDocumentPathParamsSchema,
        },
        getInvoiceAccrualMatchSummaries: {
            method: "GET",
            path: "/:workspaceId/accrual-match-summaries",
            query: invoiceAccrualMatchSummariesQuerySchema,
            responses: {
                200: invoiceAccrualMatchSummariesResponseSchema,
            },
            pathParams: InvoiceWorkspacePathParamsSchema,
        },
        getInvoiceAccrualMatches: {
            method: "GET",
            path: "/:workspaceId/:invoiceId/accrual-matches",
            responses: {
                200: documentAccrualMatchesResponseSchema,
                404: c.type<object>(),
            },
            pathParams: InvoiceDocumentPathParamsSchema,
        },
        getSuggestedAccrualMatchTransactions: {
            method: "GET",
            path: "/:workspaceId/:invoiceId/suggested-accrual-match-transactions",
            query: suggestedAccrualMatchTransactionsForInvoiceQuerySchema,
            responses: {
                200: getTransactionsResponseWithCountSchema,
                404: c.type<object>(),
            },
            pathParams: InvoiceDocumentPathParamsSchema,
        },
        getInvoiceImportedSettlements: {
            method: "GET",
            path: "/:workspaceId/:invoiceId/imported-settlements",
            responses: {
                200: documentImportedSettlementsResponseSchema,
                404: c.type<object>(),
            },
            pathParams: InvoiceDocumentPathParamsSchema,
        },
    },
    {
        pathPrefix: "/invoices",
    },
);
